/**
 * The single live store, and the two ways into it.
 *
 * React reads it through `useLive`; the Pixi scene — which is inside a lazily loaded chunk and
 * must not import React — reads it through `crowdSource()`. Both go through the same zustand
 * store, so there is never a frame where the DOM pill and the crowd on the canvas disagree about
 * whether a provider is down.
 *
 * It is a module-level singleton rather than a context because the transport is started once in
 * `main.tsx` for the whole tab: a second store would mean a second socket, and the server counts
 * sockets per IP (`docs/api.md` § 1.3).
 */

import { useStore } from 'zustand'
import { createStore } from 'zustand/vanilla'
import type { ClickTarget, ProviderId, ServerMsg, TransportStatus } from '@/net/transport'
import {
  applyMessage,
  applyPress,
  decayUnacked,
  initialLiveState,
  UNACKED_DECAY_MS,
  type CounterKey,
  type LiveState,
} from './live'
import { announceMoments, onAllClear, onBreach, onCodexReset } from './siegeEvents'
import { PEEKER_MIN_AGE_MS, PEEKER_MIN_CLICKS } from '@/codex/crowd'

export const liveStore = createStore<LiveState>()(() => initialLiveState())

/**
 * Feed one server frame in. Safe to call before `hello`; `applyMessage` drops those.
 *
 * The before/after pair is taken around the reducer rather than inside it because a breach and an
 * all-clear are *transitions*, and a reducer only ever produces a state. `announceMoments` runs
 * last, after every store subscriber has already seen the new numbers, so a listener that reads
 * the store from inside a moment handler reads the world the moment describes and not the one
 * before it.
 */
export function dispatch(msg: ServerMsg): void {
  const before = liveStore.getState()
  liveStore.setState((state) => applyMessage(state, msg), true)
  announceMoments(before, liveStore.getState(), msg)
}

/**
 * Where a press goes after the page has believed it. `main.tsx` points this at the batcher.
 *
 * A registered sink rather than an import so the store keeps knowing nothing about the wire: a
 * page rendered in a test, in the Lab or in a future demo mode presses the same button, moves the
 * same counter, and simply has nowhere to send it.
 */
let sink: ((target: ClickTarget) => void) | null = null
let decayTimer: ReturnType<typeof setTimeout> | null = null

export function setClickSink(next: ((target: ClickTarget) => void) | null): void {
  sink = next
}

/**
 * One press: believed here, then sent.
 *
 * In that order on purpose. The optimistic increment is the feedback the visitor is actually
 * clicking for, and it must not wait for a batcher, a socket, or anything that can be slow or
 * absent. The sink is told afterwards and is free to queue, clip or drop it.
 */
export function pressClick(target: ClickTarget): void {
  const now = Date.now()
  liveStore.setState((state) => applyPress(state, target, now), true)
  sink?.(target)
  armDecay(UNACKED_DECAY_MS)
}

/**
 * One timer, re-armed from the store rather than reset on every press: hammering must not
 * allocate a `setTimeout` per click, and a stale timer that fires early costs one extra read.
 */
function armDecay(delayMs: number): void {
  if (decayTimer !== null) return
  decayTimer = setTimeout(() => {
    decayTimer = null
    const now = Date.now()
    const waited = now - liveStore.getState().counters.lastPressAt
    if (waited < UNACKED_DECAY_MS) {
      armDecay(UNACKED_DECAY_MS - waited)
      return
    }
    liveStore.setState((state) => decayUnacked(state, now), true)
  }, delayMs)
}

export function setConnection(connection: TransportStatus): void {
  liveStore.setState((state) => (state.connection === connection ? state : { ...state, connection }), true)
}

/** Test seam: drop everything the page believes, without tearing down the module graph. */
export function resetLiveStore(): void {
  if (decayTimer !== null) {
    clearTimeout(decayTimer)
    decayTimer = null
  }
  sink = null
  liveStore.setState(initialLiveState(), true)
}

/** What a counter should render: the server's number plus this tab's believed presses. */
export function shownCount(state: LiveState, key: CounterKey): number {
  return state.counters.shown[key]
}

export function useLive<T>(selector: (state: LiveState) => T): T {
  return useStore(liveStore, selector)
}

/**
 * What the scene needs and nothing else: which siege a square is in and how many zombies stand at
 * its wall. `subscribe` fires on any state change and the scene compares the two values itself,
 * which is cheaper than a per-square selector subscription and cannot miss an update.
 */
export interface CrowdSource {
  ready(): boolean
  /**
   * `bc` rides along with the count because a breach is the one thing the scene cannot derive from
   * `z`: the server has no "breach" state (docs/api.md § 1.2), only a counter that goes up, and a
   * gate that has come off its hinges looks nothing like a crowd that grew.
   */
  get(provider: string): { sid: string | null; z: number; bc: number }
  /**
   * The MUST HAVE RESET counter, which is how many zombies picket OpenPlaza.
   *
   * The scene is handed the number and not the crowd size: the rule that turns one into the other
   * is `codex/crowd.ts`, and the heads over the Codex card apply the same one.
   */
  resetClicks(): number
  lastResetAt(): string | null
  subscribe(listener: () => void): () => void
  /**
   * The moments, forwarded from `siegeEvents`.
   *
   * The scene could diff `bc` for itself — it already does, to decide whether to draw a gate that
   * was broken before the visitor arrived — but the *punctuation* of a live breach (the red flash,
   * the camera shake) must fire on exactly the same event as the DOM stamp and the screen-reader
   * announcement, and must not fire for the aftermath. One producer, three subscribers.
   */
  onBreach(listener: (provider: string) => void): () => void
  onAllClear(listener: (provider: string) => void): () => void
  /**
   * The Codex reset, which belongs to the hut in OpenAI's courtyard rather than to a wall.
   *
   * It carries no provider for the same reason the frame does not: a reset is a fact about the
   * world, not about a square. The scene decides where to put the confetti.
   */
  onCodexReset(listener: () => void): () => void
}

const NO_CROWD = { sid: null, z: 0, bc: 0 }

export function crowdSource(): CrowdSource {
  return {
    ready() {
      return liveStore.getState().helloReceived
    },
    get(provider) {
      const square = liveStore.getState().squares[provider as ProviderId]
      if (!square || square.phase !== 'siege') return NO_CROWD
      return { sid: square.sid, z: square.zombies, bc: square.breaches }
    },
    /**
     * What the picket outside OpenPlaza is sized from.
     *
     * `counters.shown.reset` and not the raw server number, because it is the one on the screen:
     * the odometer over the button and the heads over the card both draw the optimistic counter,
     * and a courtyard counting the server's would be a third answer to "how many of us are there".
     */
    resetClicks() {
      return liveStore.getState().counters.shown.reset
    },
    lastResetAt() {
      return liveStore.getState().lastReset?.at ?? null
    },
    subscribe(listener) {
      let timer: ReturnType<typeof setTimeout> | null = null
      const schedulePicket = () => {
        if (timer !== null) {
          clearTimeout(timer)
          timer = null
        }
        const state = liveStore.getState()
        if (state.counters.shown.reset < PEEKER_MIN_CLICKS) return
        const resetAt = Date.parse(state.lastReset?.at ?? '')
        if (!Number.isFinite(resetAt)) return
        const delay = resetAt + PEEKER_MIN_AGE_MS - Date.now()
        if (delay > 0) timer = setTimeout(listener, delay)
      }
      schedulePicket()
      const off = liveStore.subscribe(() => {
        listener()
        schedulePicket()
      })
      return () => {
        off()
        if (timer !== null) clearTimeout(timer)
      }
    },
    onBreach(listener) {
      return onBreach((moment) => listener(moment.provider))
    },
    onAllClear(listener) {
      return onAllClear((moment) => listener(moment.provider))
    },
    onCodexReset(listener) {
      return onCodexReset(() => listener())
    },
  }
}
